import React, { useState } from 'react';
import { Theme } from '../../../../Enums/Theme.enum';
import styles from './LaboratoryResultsToDescribeListItem.module.scss';
import LaboratoryResultDescribeModal from '../LaboratoryResultDescribe/LaboratoryResultDescribeModal';
import {
	LaboratoryResultMise,
	LaboratoryResultMiseStatus,
	useAssignLaboratoryResultMiseMutation,
	useLazyLaboratoryResultMiseQuery,
	useUnassignLaboratoryResultMiseMutation,
} from '../../../../graphql/graphql';
import useApiClient from '../../../Base/Hooks/useApiClient';
import { useAppContext } from '../../../../Services/AppContext';
import LinkButton from '../../../../Components/Button/LinkButton';
import OverlayLoading from '../../../../Components/OverlayLoading';
import useLaboratoryResultMiseHelper from '../../Hooks/useLaboratoryResultMiseHelper';
import Text from '../../../../Components/Base/Text/Text';
import LaboratoryResultsToDescribeTimeout from './LaboratoryResultsToDescribeTimeout';
import MediaViewerModal from '../../../../Components/MediaViewer/MediaViewerModal';
import { prepareDocumentationFileForMediaViewer } from '../../../Ecard/Services/eCardFile.service';
import FormattedMomentDate, {
	DateFormat,
} from '../../../Base/Components/FormattedMomentDate/FormattedMomentDate';
import SavingSpinner from '../../../../Components/Base/Loading/SavingSpinner';
import { AvResult } from '../../../Base/Enums/AvResult.enum';
import useSocket from '../../../Base/Hooks/useSocket';
import { Socket } from '../../../Base/Enums/Socket.enum';

interface Props {
	item: LaboratoryResultMise;
	onUpdate: (item: LaboratoryResultMise) => void;
	reloadList: () => Promise<void>;
}

const LaboratoryResultsToDescribeListItem: React.FC<Props> = ({
	item,
	onUpdate,
	reloadList,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isMediaViewerOpen, setIsMediaViewerOpen] = useState(false);
	const { displayGQLError, displayError } = useApiClient();
	const [assignLaboratoryResult] = useAssignLaboratoryResultMiseMutation();
	const [unassignLaboratoryResult] = useUnassignLaboratoryResultMiseMutation();
	const [getLaboratoryResultMise] = useLazyLaboratoryResultMiseQuery();
	const { userData, socketUrl } = useAppContext();
	const isAssignedToLoggedUser = userData.id === item.doctorId;
	const { patient } = useLaboratoryResultMiseHelper(item);

	const refetchItem = async () => {
		const data = await getLaboratoryResultMise({
			where: { id: item.id },
		}).unwrap();
		if (data.laboratoryResultMise) {
			onUpdate(data.laboratoryResultMise as LaboratoryResultMise);
		}
	};

	const handleAssign = async () => {
		setIsLoading(true);
		try {
			const response = await assignLaboratoryResult({
				input: { laboratoryResultMiseId: item.id },
			});
			displayGQLError(response);
			if ('data' in response) {
				setIsOpen(true);
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	const handleDescribe = async () => {
		if (isAssignedToLoggedUser) {
			setIsOpen(true);
		} else {
			await handleAssign();
		}
	};

	const handleCancelAssign = async () => {
		setIsLoading(true);
		try {
			const response = await unassignLaboratoryResult({
				input: { laboratoryResultMiseId: item.id },
			});
			displayGQLError(response);
			if ('data' in response) {
				await reloadList();
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	const handleOnClose = async () => {
		setIsOpen(false);
		await reloadList();
	};

	const onSocket = async (data: {
		avResult: string;
		updatedData: { avResult?: AvResult };
	}) => {
		if (
			data.avResult === item.file?.file?.id &&
			data.updatedData.avResult === AvResult.CLEAN
		) {
			await refetchItem();
		}
	};

	useSocket(Socket.MESSAGE, data => onSocket(data));

	return (
		<>
			{isLoading && <OverlayLoading />}
			<div className={styles.item}>
				<div className={styles.date}>
					<FormattedMomentDate
						date={
							item.status === LaboratoryResultMiseStatus.Done
								? item.noteAt
								: item.createdAt
						}
						format={DateFormat.DATE_TIME}
					/>
				</div>
				<div className={styles.patient}>
					<div className={styles.title}>{patient}</div>
					{item.status !== LaboratoryResultMiseStatus.Done && (
						<LaboratoryResultsToDescribeTimeout laboratoryResultMise={item} />
					)}
				</div>
				<div className={styles.content}>
					<Text
						className={styles.title}
						tx='component.laboratoryResultsToDescribeListItem.laboratoryResults'
					/>
					<div className={styles.subtitle}>
						{item.laboratoryResult?.laboratory?.name}
					</div>
				</div>
				<div className={styles.controls}>
					{item.status === LaboratoryResultMiseStatus.Done ? (
						<>
							{item.file?.file?.processing ? (
								<SavingSpinner tx='base.documentIsProcessing' />
							) : (
								<LinkButton
									title='component.laboratoryResultsToDescribeListItem.detail'
									theme={Theme.BLUE}
									onClick={() => setIsMediaViewerOpen(true)}
									firstUpper
								/>
							)}
						</>
					) : (
						<>
							<LinkButton
								title='component.laboratoryResultsToDescribeListItem.describe'
								theme={Theme.BLUE}
								onClick={handleDescribe}
								firstUpper
							/>
							{isAssignedToLoggedUser && (
								<LinkButton
									title='component.laboratoryResultsToDescribeListItem.cancelAssign'
									theme={Theme.RED}
									onClick={handleCancelAssign}
								/>
							)}
						</>
					)}
				</div>
			</div>
			{isOpen && (
				<LaboratoryResultDescribeModal
					isOpen
					onClose={handleOnClose}
					laboratoryResultMise={item}
				/>
			)}
			{isMediaViewerOpen && item.file && (
				<MediaViewerModal
					opened
					onClose={() => setIsMediaViewerOpen(false)}
					file={prepareDocumentationFileForMediaViewer(item.file, socketUrl)}
					isSignable={item.file.canBeSigned ?? false}
				/>
			)}
		</>
	);
};

export default LaboratoryResultsToDescribeListItem;
